import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import { useTranslation } from 'react-i18next'

import { TestCreateSection } from '../TestCreateSection'
import type { TestSkillGoal } from '../testSettingsTypes'

/**
 * Цель проверки — только чтение: она выводится из отображения и формата ответа.
 *
 * Выбирать её отдельно нельзя намеренно: матрица `prompt × answer` и так задаёт
 * набор навыков однозначно, а второй орган управления давал бы настройку,
 * которая противоречит первым двум секциям.
 */
export function TestCheckGoalSection({
  skills,
  hasCompatibleAnswerTypes,
  excludeDuplicateWords,
  hint,
}: {
  skills: TestSkillGoal[]
  /** Пустой набор форматов — секция не может ничего вывести и объясняет почему. */
  hasCompatibleAnswerTypes: boolean
  /** Только группа: одно и то же слово не должно попасть двум ученикам сразу. */
  excludeDuplicateWords?: {
    value: boolean
    onChange: (value: boolean) => void
  }
  /** Пояснение, откуда берётся цель: она не выбирается руками. */
  hint?: string
}) {
  const { t } = useTranslation()
  const empty = skills.length === 0 || !hasCompatibleAnswerTypes

  return (
    <TestCreateSection
      id="skills"
      icon={<TrackChangesIcon fontSize="small" />}
      title={t('tests.createForm.sectionCheckGoal')}
    >
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {hint}
        </Typography>
      ) : null}
      {empty ? (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {t('tests.createForm.noActiveSkillsNeedSelectionHint', {
            display: t('tests.createForm.sectionDisplay'),
            answerFormat: t('tests.createForm.sectionAnswerFormat'),
          })}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1,
          }}
        >
          {skills.map((skillId) => (
            <Paper
              key={skillId}
              variant="outlined"
              sx={(theme) => ({
                overflow: 'hidden',
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22),
              })}
            >
              <Stack spacing={0.25} sx={{ p: 1.25 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    fontWeight: 600,
                    color:
                      theme.palette.mode === 'light'
                        ? theme.palette.primary.dark
                        : theme.palette.primary.light,
                  })}
                >
                  {t(`tests.createForm.skillGoals.${skillId}.title`)}
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  sx={(theme) => ({
                    display: 'block',
                    lineHeight: 1.35,
                    color:
                      theme.palette.mode === 'light'
                        ? alpha(theme.palette.primary.dark, 0.92)
                        : alpha(theme.palette.primary.light, 0.9),
                  })}
                >
                  {t(`tests.createForm.skillGoals.${skillId}.desc`)}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      {excludeDuplicateWords ? (
        <FormControlLabel
          control={
            <Switch
              checked={excludeDuplicateWords.value}
              onChange={(e) => excludeDuplicateWords.onChange(e.target.checked)}
            />
          }
          label={t('tests.createForm.excludeDuplicateWords')}
        />
      ) : null}
    </TestCreateSection>
  )
}
